import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Plus, Briefcase, Calendar, Users, MapPin, CheckCircle, Camera, ChevronDown, ChevronUp } from "lucide-react";
import PhotoUpload from "../components/PhotoUpload";

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-700",
  in_progress: "bg-blue-100 text-blue-700",
  completed: "bg-green-100 text-green-700",
};

export default function JobsPage() {
  const [jobs, setJobs] = useState([]);
  const [leads, setLeads] = useState([]);
  const [showNewJob, setShowNewJob] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [newJob, setNewJob] = useState({ lead_id: "", assigned_crew: "", installation_date: "", material_list: "", work_notes: "" });
  const [editingJob, setEditingJob] = useState(null);
  const [expandedJob, setExpandedJob] = useState(null);
  const [jobPhotos, setJobPhotos] = useState({});

  const fetchJobs = useCallback(async () => {
    try { const { data } = await api.get("/jobs"); setJobs(data); } catch (err) { console.error("Failed to load jobs:", err); }
  }, []);

  const fetchLeads = useCallback(async () => {
    try { const { data } = await api.get("/leads?stage=approved"); setLeads(data); } catch (err) { console.error("Failed to load leads:", err); }
  }, []);

  const fetchJobPhotos = useCallback(async (jobId) => {
    try {
      const [before, after] = await Promise.all([
        api.get(`/photos/job_before/${jobId}`),
        api.get(`/photos/job_after/${jobId}`),
      ]);
      setJobPhotos((prev) => ({ ...prev, [jobId]: { before: before.data, after: after.data } }));
    } catch (err) { console.error("Failed to load job photos:", err); }
  }, []);

  useEffect(() => { fetchJobs(); fetchLeads(); }, [fetchJobs, fetchLeads]);
  useEffect(() => {
    const leadId = searchParams.get("lead_id");
    if (leadId) {
      setNewJob(prev => ({ ...prev, lead_id: leadId }));
      setShowNewJob(true);
      window.history.replaceState({}, "", "/jobs");
    }
  }, [searchParams]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newJob.lead_id) { toast.error("Select a lead"); return; }
    try {
      await api.post("/jobs", newJob);
      toast.success("Job created");
      setShowNewJob(false);
      setNewJob({ lead_id: "", assigned_crew: "", installation_date: "", material_list: "", work_notes: "" });
      fetchJobs();
    } catch { toast.error("Failed to create job"); }
  };

  const handleStatusUpdate = async (jobId, status) => {
    try {
      await api.put(`/jobs/${jobId}`, { status });
      toast.success("Status updated");
      fetchJobs();
    } catch { toast.error("Failed to update"); }
  };

  return (
    <div className="space-y-6" data-testid="jobs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-zinc-900 tracking-tight">Jobs</h1>
          <p className="text-sm text-zinc-500 mt-1">{jobs.length} total jobs</p>
        </div>
        <Button onClick={() => setShowNewJob(true)} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="create-job-btn">
          <Plus className="w-4 h-4 mr-2" /> New Job
        </Button>
      </div>

      {jobs.length === 0 ? (
        <Card className="border border-zinc-200 shadow-none">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Briefcase className="w-12 h-12 text-zinc-300 mb-3" />
            <p className="text-zinc-500 text-sm">No jobs yet. Create one from an approved lead.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {jobs.map((job) => (
            <Card key={job.id} className="border border-zinc-200 shadow-none hover:border-orange-300 transition-colors" data-testid={`job-card-${job.id}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-bold text-zinc-900">{job.lead_name || "Job"}</p>
                    {job.lead_address && <p className="text-xs text-zinc-500 flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" /> {job.lead_address}</p>}
                  </div>
                  <Badge className={`text-xs capitalize ${STATUS_COLORS[job.status] || "bg-zinc-100 text-zinc-600"}`}>
                    {job.status?.replace(/_/g, " ")}
                  </Badge>
                </div>
                {job.assigned_crew && <p className="text-xs text-zinc-600 flex items-center gap-1"><Users className="w-3 h-3" /> {job.assigned_crew}</p>}
                {job.installation_date && <p className="text-xs text-zinc-600 flex items-center gap-1"><Calendar className="w-3 h-3" /> {job.installation_date}</p>}
                {job.work_notes && <p className="text-xs text-zinc-500 line-clamp-2">{job.work_notes}</p>}
                <div className="flex gap-2 pt-1">
                  {job.status === "pending" && (
                    <Button size="sm" variant="outline" onClick={() => handleStatusUpdate(job.id, "in_progress")} className="text-xs flex-1" data-testid={`start-job-${job.id}`}>
                      Start Job
                    </Button>
                  )}
                  {job.status === "in_progress" && (
                    <Button size="sm" onClick={() => handleStatusUpdate(job.id, "completed")} className="text-xs flex-1 bg-green-600 hover:bg-green-700 text-white" data-testid={`complete-job-${job.id}`}>
                      <CheckCircle className="w-3 h-3 mr-1" /> Complete
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { const isExpanded = expandedJob === job.id; setExpandedJob(isExpanded ? null : job.id); if (!isExpanded) fetchJobPhotos(job.id); }}
                    className="text-xs"
                    data-testid={`photos-toggle-${job.id}`}
                  >
                    <Camera className="w-3 h-3 mr-1" /> Photos
                    {expandedJob === job.id ? <ChevronUp className="w-3 h-3 ml-0.5" /> : <ChevronDown className="w-3 h-3 ml-0.5" />}
                  </Button>
                </div>
                {/* Expandable Photos Section */}
                {expandedJob === job.id && (
                  <div className="space-y-3 pt-2 border-t border-zinc-100 animate-fade-in">
                    <div>
                      <p className="text-xs font-semibold text-zinc-600 mb-1.5">Before Photos</p>
                      <PhotoUpload
                        entityType="job_before"
                        entityId={job.id}
                        photoType="before"
                        photos={jobPhotos[job.id]?.before || []}
                        onPhotosChange={() => fetchJobPhotos(job.id)}
                      />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-zinc-600 mb-1.5">After Photos</p>
                      <PhotoUpload
                        entityType="job_after"
                        entityId={job.id}
                        photoType="after"
                        photos={jobPhotos[job.id]?.after || []}
                        onPhotosChange={() => fetchJobPhotos(job.id)}
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* New Job Dialog */}
      <Dialog open={showNewJob} onOpenChange={setShowNewJob}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="text-lg font-bold">Create New Job</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Lead</Label>
              <Select value={newJob.lead_id} onValueChange={(v) => setNewJob({ ...newJob, lead_id: v })}>
                <SelectTrigger data-testid="job-lead-select"><SelectValue placeholder="Select approved lead" /></SelectTrigger>
                <SelectContent>
                  {leads.map(l => <SelectItem key={l.id} value={l.id}>{l.name} - {l.address || l.phone}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Crew</Label>
                <Input value={newJob.assigned_crew} onChange={(e) => setNewJob({ ...newJob, assigned_crew: e.target.value })} placeholder="Crew name" data-testid="job-crew" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Install Date</Label>
                <Input type="date" value={newJob.installation_date} onChange={(e) => setNewJob({ ...newJob, installation_date: e.target.value })} data-testid="job-date" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Material List</Label>
              <Textarea value={newJob.material_list} onChange={(e) => setNewJob({ ...newJob, material_list: e.target.value })} placeholder="Materials needed..." rows={3} data-testid="job-materials" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Work Notes</Label>
              <Textarea value={newJob.work_notes} onChange={(e) => setNewJob({ ...newJob, work_notes: e.target.value })} placeholder="Notes..." rows={2} data-testid="job-notes" />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setShowNewJob(false)} className="flex-1">Cancel</Button>
              <Button type="submit" className="flex-1 bg-orange-600 hover:bg-orange-700 text-white" data-testid="submit-job-btn">Create Job</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
